import * as React from 'react'
import { ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'

import { useFiltersApi } from '../../../hooks'
import {
    formatFirstFacilityAddressField,
    formatSecondFacilityAddressField,
} from '../../../utils/formatFacilityAddress'

import { FilterProps } from '../../common/Filter/types'
import { SelectFilter } from '../../common/Filter/SelectFilter/SelectFilter'
import { type Option } from '../../common/Form/types'

import { useFilterLabels } from './useFilterLabels'
import { GetFacilitiesRequestParams } from '@/hooks/useApi/useFiltersApi/types'
import { type Facility } from '@/hooks/useApi/types'

export type FacilityFilterProps = {
    name: string
    onChange: (facility: string[]) => void
    value: string[]
    defaultValue: string[]
    disabled?: boolean
    valueLabel?: string
    params: Partial<GetFacilitiesRequestParams>
    className?: string
} & Pick<FilterProps, 'containerRef'>

export const FacilityFilter: React.FC<FacilityFilterProps> = (props) => {
    const { disabled, value, defaultValue, name, params, onChange } = props
    const { useFacilitiesQuery } = useFiltersApi()

    const query = useFacilitiesQuery(params)
    const { data, isLoading, isError, isFetched, isFetching } = query
    const labels = useFilterLabels()
    const { t } = useTranslation('translations')

    const optionMap = React.useMemo<Map<string, Facility>>(() => {
        if (!data) return new Map<string, Facility>()
        return new Map(
            data.map((facility) => [facility.facilityId.toString(), facility])
        )
    }, [data])
    const options: Option[] | undefined = data?.map((facility) => {
        return {
            value: facility.facilityId.toString(),
            label: facility.facilityName,
        }
    })

    const renderRichLabel = React.useCallback(
        (
            option: Option,
            highlightMatchingText?: (
                input: string
            ) => string | React.JSX.Element
        ) => {
            if (typeof option.value !== 'string') return option.label

            const rawOption = optionMap.get(option.value)

            if (rawOption) {
                const firstAddressField = formatFirstFacilityAddressField(
                    rawOption.facilityAddress
                )

                const secondAddressField = formatSecondFacilityAddressField(
                    rawOption.facilityAddress
                )

                return (
                    <div className="space-y-0.5">
                        <div>
                            {highlightMatchingText?.(rawOption.facilityName) ??
                                rawOption.facilityName}
                        </div>
                        <div className="text-body-small text-subtle">
                            {highlightMatchingText?.(firstAddressField) ??
                                firstAddressField}
                        </div>
                        {secondAddressField && (
                            <div className="text-body-small text-subtle">
                                {highlightMatchingText?.(secondAddressField) ??
                                    secondAddressField}
                            </div>
                        )}
                    </div>
                )
            }

            return option.label
        },
        [optionMap]
    )

    const useSearchParams = React.useCallback(
        (searchValue: string) => ({
            options: options,
            compareFn: (option: Option) => {
                if (typeof option.value !== 'string') return false

                const rawOption = optionMap.get(option.value)
                if (!rawOption) {
                    return false
                }
                const firstAddressField = formatFirstFacilityAddressField(
                    rawOption.facilityAddress
                )
                const secondAddressField = formatSecondFacilityAddressField(
                    rawOption.facilityAddress
                )

                return (
                    rawOption.facilityName
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim()) ||
                    firstAddressField
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim()) ||
                    secondAddressField
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim())
                )
            },
        }),
        [options, optionMap]
    )

    const passedProps: ComponentProps<typeof SelectFilter> = {
        name,
        disabled: !!disabled,
        onChange: onChange,
        searchConfig: {
            name: `${name}-search`,
            placeholder: t('filters.facility.searchPlaceholder'),
        },
        config: {
            value: value,
            options: options,
        },
        value: value,
        defaultValue: defaultValue,
        renderRichLabel,
        useSearchParams,
        label: t('filters.facility.label'),
        ...labels,
        isLoading: isLoading || isFetching || !isFetched,
        isError,
        valueLabel: props.valueLabel,
        containerRef: props.containerRef,
        className: props.className,
    }

    return <SelectFilter {...passedProps} />
}
